import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function ExpirationReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [locationFilter, setLocationFilter] = useState('');
  const [medicationFilter, setMedicationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [daysMin, setDaysMin] = useState('');
  const [daysMax, setDaysMax] = useState('');

  const [locations, setLocations] = useState([]);
  const [medications, setMedications] = useState([]);

  const LOCATION_TABLES = [
    { table: 'mayo_21', name: 'Mayo 21' },
    { table: 'mayo_22', name: 'Mayo 22' },
    { table: 'mayo_23', name: 'Mayo 23' },
    { table: 'mayo_24', name: 'Mayo 24' },
    { table: 'mayo_25', name: 'Mayo 25' }
  ];

  const MEDICATION_FIELDS = [
    'fentanyl_exp',
    'ketamine_exp',
    'etomidate_exp',
    'midazolam10mg_2ml_exp',
    'midazolam5mg_5ml_exp'
  ];

  const formatMedicationName = (key) => {
    const specialCases = {
      'midazolam10mg_2ml': 'Midazolam 10mg/2mL',
      'midazolam5mg_5ml': 'Midazolam 5mg/5mL'
    };

    if (specialCases[key]) return specialCases[key];

    return key
      .replace(/_exp$/, '')
      .replace(/_/g, ' ')
      .replace(/(\w+)/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  };

  const calculateDaysRemaining = (expDateStr) => {
    if (!expDateStr) return null;

    const expDate = new Date(expDateStr);
    if (isNaN(expDate.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);

    return Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
  };

  const getStatus = (days) => {
    if (days === null) return { label: 'Unknown', class: 'status-unknown' };
    if (days <= 0) return { label: 'Expired/Today', class: 'status-expired' };
    if (days <= 7) return { label: '1-7 Days', class: 'status-critical' };
    if (days <= 30) return { label: '8-30 Days', class: 'status-warning' };
    return { label: '31+ Days', class: 'status-good' };
  };

  const loadReport = async () => {
    setLoading(true);

    try {
      const promises = LOCATION_TABLES.map(async ({ table, name }) => {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error(`Error querying ${table}:`, error);
          return { location: name, items: [] };
        }

        if (!data || data.length === 0) {
          return { location: name, items: [] };
        }

        const record = data[0];
        const items = [];

        MEDICATION_FIELDS.forEach(field => {
          const expDate = record[field];
          if (expDate) {
            const medKey = field.replace(/_exp$/, '');
            const days = calculateDaysRemaining(expDate);
            const status = getStatus(days);

            items.push({
              location: name,
              medication: formatMedicationName(medKey),
              medKey,
              expDate: new Date(expDate).toLocaleDateString('en-US'),
              days,
              status
            });
          }
        });

        return { location: name, items };
      });

      const results = await Promise.all(promises);
      const allRows = results.flatMap(r => r.items);

      // Sort by urgency
      allRows.sort((a, b) => {
        const scoreA = a.days === null ? 5 : (a.days <= 0 ? 0 : a.days <= 7 ? 1 : a.days <= 30 ? 2 : 3);
        const scoreB = b.days === null ? 5 : (b.days <= 0 ? 0 : b.days <= 7 ? 1 : b.days <= 30 ? 2 : 3);

        if (scoreA !== scoreB) return scoreA - scoreB;

        const daysA = a.days === null ? 99999 : a.days;
        const daysB = b.days === null ? 99999 : b.days;
        return daysA - daysB;
      });

      setAllData(allRows);
      setFilteredData(allRows);

      // Extract unique locations and medications for filters
      const uniqueLocs = [...new Set(allRows.map(r => r.location))].sort();
      const uniqueMeds = [...new Set(allRows.map(r => r.medication))].sort();

      setLocations(uniqueLocs);
      setMedications(uniqueMeds);

    } catch (error) {
      console.error('Error loading expiration report:', error);
      alert('Error loading report.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = allData;

    if (locationFilter) {
      filtered = filtered.filter(r => r.location === locationFilter);
    }

    if (medicationFilter) {
      filtered = filtered.filter(r => r.medication === medicationFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(r => r.status.label === statusFilter);
    }

    if (daysMin !== '') {
      const min = parseInt(daysMin);
      filtered = filtered.filter(r => r.days !== null && r.days >= min);
    }

    if (daysMax !== '') {
      const max = parseInt(daysMax);
      filtered = filtered.filter(r => r.days !== null && r.days <= max);
    }

    setFilteredData(filtered);
  };

  const resetFilters = () => {
    setLocationFilter('');
    setMedicationFilter('');
    setStatusFilter('');
    setDaysMin('');
    setDaysMax('');
    setFilteredData(allData);
  };

  const exportToCSV = () => {
    if (filteredData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Location', 'Medication', 'Expiration Date', 'Days Remaining', 'Status'];
    const csvData = [
      headers.join(','),
      ...filteredData.map(row =>
        [
          row.location,
          row.medication,
          row.expDate,
          row.days === null ? '' : row.days,
          row.status.label
        ]
          .map(field => `"${field}"`)
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `expiration_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (isAdmin) {
      loadReport();
    }
  }, [isAdmin]);

  // Admin check
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div style={{ padding: '80px 20px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          style={{
            padding: '8px 16px',
            border: '2px solid #0E3293',
            borderRadius: '6px',
            background: 'white',
            color: '#0E3293',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Admin
        </button>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0E3293 0%, #1A4DBB 100%)',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '20px',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(14, 50, 147, 0.15)'
        }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px' }}>Medication Expiration Report</h1>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            Newest record from each location. Sorted by urgency.
          </p>
        </div>

        {/* Controls */}
        <div style={{
          background: '#fff',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '15px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={loadReport}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#0E3293',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            onClick={exportToCSV}
            disabled={filteredData.length === 0}
            style={{
              padding: '10px 20px',
              background: filteredData.length === 0 ? '#ccc' : '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Download CSV
          </button>
        </div>

        {/* Filters */}
        <div style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
            marginBottom: '15px'
          }}>
            {/* Location Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                Location
              </label>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">All</option>
                {locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            {/* Medication Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                Medication
              </label>
              <select
                value={medicationFilter}
                onChange={(e) => setMedicationFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">All</option>
                {medications.map(med => (
                  <option key={med} value={med}>{med}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">All</option>
                <option value="Expired/Today">Expired/Today</option>
                <option value="1-7 Days">1-7 Days</option>
                <option value="8-30 Days">8-30 Days</option>
                <option value="31+ Days">31+ Days</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>

            {/* Days Range */}
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '14px' }}>
                Days Remaining
              </label>
              <div style={{ display: 'flex', gap: '5px' }}>
                <input
                  type="number"
                  placeholder="Min"
                  value={daysMin}
                  onChange={(e) => setDaysMin(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={daysMax}
                  onChange={(e) => setDaysMax(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={applyFilters}
              style={{
                padding: '8px 20px',
                background: '#0E3293',
                color: '#fff',
                border: '1px solid #0E3293',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Apply Filters
            </button>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 20px',
                background: '#fff',
                color: '#6c757d',
                border: '1px solid #6c757d',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        {!loading && (
          <>
            <div className="report-table-container">
              <div className="table-scroll">
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#fff',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <thead>
                    <tr style={{ background: '#0E3293', color: '#fff' }}>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '120px' }}>
                        Location
                      </th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '180px' }}>
                        Medication
                      </th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '120px' }}>
                        Expiration Date
                      </th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '120px' }}>
                        Days Remaining
                      </th>
                      <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '120px' }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          No expiration data found.
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((row, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '12px 15px' }}>{row.location}</td>
                          <td style={{ padding: '12px 15px' }}>{row.medication}</td>
                          <td style={{ padding: '12px 15px' }}>{row.expDate}</td>
                          <td style={{ padding: '12px 15px' }}>
                            {row.days === null ? 'N/A' : row.days}
                          </td>
                          <td style={{ padding: '12px 15px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '4px',
                              fontSize: '13px',
                              fontWeight: '600',
                              background:
                                row.status.class === 'status-expired' ? '#ffebee' :
                                row.status.class === 'status-critical' ? '#ffcdd2' :
                                row.status.class === 'status-warning' ? '#fff9c4' :
                                row.status.class === 'status-good' ? '#c8e6c9' :
                                '#e0e0e0',
                              color:
                                row.status.class === 'status-expired' ? '#c62828' :
                                row.status.class === 'status-critical' ? '#c62828' :
                                row.status.class === 'status-warning' ? '#f57f17' :
                                row.status.class === 'status-good' ? '#2e7d32' :
                                '#757575'
                            }}>
                              {row.status.label}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Legend */}
            <div style={{
              marginTop: '15px',
              padding: '15px',
              background: '#fff',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#666',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <strong>Legend:</strong> Red highlight = Expired/Today • Red = 1-7 days • Yellow = 8-30 days • Green = 31+ days
            </div>
          </>
        )}
      </div>

      <footer className="footer">
        <p className="text-small">
          Expiration Report - NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC
        </p>
      </footer>
    </div>
  );
}

export default ExpirationReport;
